/**
 * The stages, in order, and the rules that read that order.
 *
 * Declared HERE rather than imported from the api module, because this is
 * domain logic and that module reads `import.meta.env` — importing it made
 * these rules untestable outside a browser build, which is exactly backwards
 * for the one part of this screen with real logic in it.
 */
export const FLAG_STAGES = ['off', 'internal', 'alpha', 'beta', 'general'] as const;
export type FlagStage = (typeof FLAG_STAGES)[number];

/**
 * Whether moving to `to` would take a feature away from somebody who has it.
 *
 * The stages are ordered and each admits everyone the one before it did, so a
 * move backwards is the only way a change here can REMOVE access. That deserves
 * a warning: rolling a flag back is a legitimate thing to do in an incident,
 * and a legitimate thing to do by accident when reaching for the wrong option
 * in a list.
 */
export function isRetreat(from: FlagStage, to: FlagStage): boolean {
  return FLAG_STAGES.indexOf(to) < FLAG_STAGES.indexOf(from);
}

/**
 * How many accounts a stage actually reaches, for the summary on each row.
 *
 * Counts the OVERRIDES too, because they are the part somebody forgets. A flag
 * at `off` with four accounts switched on is live for four accounts, and a row
 * that just said "off" would be lying about it.
 */
export function reach(state: {
  stage: FlagStage;
  alphaTenantIds: string[];
  betaTenantIds: string[];
  overrides: Record<string, boolean>;
}): { byStage: number | 'everyone'; forcedOn: number; forcedOff: number } {
  const forcedOn = Object.values(state.overrides).filter(Boolean).length;
  const forcedOff = Object.values(state.overrides).filter((v) => v === false).length;

  if (state.stage === 'general') return { byStage: 'everyone', forcedOn, forcedOff };
  if (state.stage === 'beta') {
    // Beta admits the alpha accounts as well, so counting only the beta list
    // would under-report who has it.
    const both = new Set([...state.alphaTenantIds, ...state.betaTenantIds]);
    return { byStage: both.size, forcedOn, forcedOff };
  }
  if (state.stage === 'alpha') return { byStage: state.alphaTenantIds.length, forcedOn, forcedOff };
  return { byStage: 0, forcedOn, forcedOff };
}
